import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IFormParams, IStatusOption } from 'pages/forms/types';
import React from 'react';
import { useFormFetcher } from '../useFormFilters';
import moment from 'moment';
import { AuthContext } from 'contexts/AuthContext';
import { useSuppliersContext } from 'contexts/SuppliersContext';
import { retrieveValidParams } from 'utils/http';

const transformSelectedOptionsFilter = (list: IStatusOption[], selected: number[]) => {
  return list.reduce(
    (acc, { key, id }) => ({ ...acc, [key]: selected.includes(id) ? 'S' : undefined }),
    {} as Record<string, string | undefined>
  );
};

export const useFormpage = () => {
  const { currentSupplierId, supplierLoading } = useSuppliersContext();
  const get = useFormFetcher();
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const [selectedOptionsCheckboxes, setSelectedOptionsCheckboxes] = useState<number[]>([]);

  const [selectedStatusOptions, setSelectedStatusOptions] = useState<IStatusOption[]>([
    { id: 1, description: 'Rascunho', checked: false, key: 'draft' },
    { id: 3, description: 'Publicado', checked: false, key: 'published' },
    { id: 0, description: 'Cancelado', checked: false, key: 'canceled' },
    { id: 2, description: 'Inativo', checked: false, key: 'inactive' }
  ]);

  const {
    user: { isVisaoTok }
  } = useContext(AuthContext);

  const handleOptionChange = (idButtonChecked: number) => {
    const items = selectedStatusOptions.map((item) => {
      return item.id === Number(idButtonChecked) ? { ...item, checked: !item.checked } : item;
    });

    setSelectedStatusOptions(items);
  };

  const handleOptionClick = () => {
    const checkedItens = selectedStatusOptions.filter(({ checked }) => (checked ? true : false));
    const idList = checkedItens.map(({ id }) => id);

    setSelectedOptionsCheckboxes(idList);
    handleStatusFilterModal();
    defineFilters(transformSelectedOptionsFilter(selectedStatusOptions, idList));
  };

  const resetSelectedStatusOptions = () => {
    setSelectedStatusOptions((prev) => prev.map((item) => ({ ...item, checked: false })));
  };

  const retrieveStatusFilter = useMemo(
    () => transformSelectedOptionsFilter(selectedStatusOptions, selectedOptionsCheckboxes),
    [selectedStatusOptions, selectedOptionsCheckboxes]
  );

  const handleStatusFilterModal = () => {
    setIsStatusModalOpen(!isStatusModalOpen);
  };

  const [page, setPage] = useState(1);
  const handleNextPage = () => {
    setPage((prev) => prev + 1);
  };
  const handlePreviousPage = () => {
    if (page > 1) {
      setPage((prev) => prev - 1);
    }
  };

  const tableChangePage = (page: number) => {
    setPage(page + 1);
  };

  const [qtItemsPage, setQtItemsPage] = useState(5);

  const [dtInicVig, setDtInicVig] = useState<Date | null>(null);
  const handleDtInicVigChange = (value: Date | null) => {
    setDtInicVig(value);
  };

  const [dtFimVig, setDtFimVig] = useState<Date | null>(null);
  const handleDtFimVigChange = (value: Date | null) => {
    setDtFimVig(value);
  };

  const [search, setSearch] = useState('');
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = event.target;
    setSearch(target.value);
  };
  const handleClear = () => {
    setSearch('');
    setDtInicVig(null);
    setDtFimVig(null);
    setPage(1);
    resetSelectedStatusOptions();
    setSelectedOptionsCheckboxes([]);
  };

  const [filter, setFilter] = useState<IFormParams>({});

  const defineFilters = useCallback(
    (params: IFormParams = {}) => {
      const startDate = dtInicVig ? moment.utc(dtInicVig).format('DD/MM/YYYY') : undefined;
      const endDate = dtFimVig ? moment.utc(dtFimVig).format('DD/MM/YYYY') : undefined;
      setFilter({
        ...retrieveStatusFilter,
        pageNumber: 1,
        nrRecordPage: qtItemsPage,
        dtFimVig: endDate,
        dtInicVig: startDate,
        isVisaoTok: isVisaoTok ? 'S' : 'N',
        supplierId: !isVisaoTok ? currentSupplierId : undefined,
        search,
        ...params
      });
      setPage(1);
    },
    [retrieveStatusFilter, qtItemsPage, search, dtFimVig, dtInicVig, isVisaoTok, currentSupplierId]
  );

  const handleClearSearch = useCallback(() => {
    handleClear();
    defineFilters({
      pageNumber: 1,
      nrRecordPage: 5,
      dtFimVig: undefined,
      dtInicVig: undefined,
      search: undefined,
      published: undefined,
      draft: undefined,
      active: undefined,
      inactive: undefined
    });
  }, [defineFilters]);

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      pageNumber: page,
      nrRecordPage: qtItemsPage,
      isVisaoTok: isVisaoTok ? 'S' : 'N',
      supplierId: !isVisaoTok ? currentSupplierId : undefined
    }));
  }, [page, qtItemsPage, isVisaoTok, currentSupplierId]);

  useEffect(() => {
    if (!supplierLoading) {
      const params = retrieveValidParams(filter);
      get.loadForms(params);
    }
  }, [filter, get.loadForms, supplierLoading]);

  return {
    ...get,
    handleOptionChange,
    handleOptionClick,
    handleStatusFilterModal,
    isStatusModalOpen,
    selectedOptionsCheckboxes,
    setSelectedOptionsCheckboxes,
    selectedStatusOptions,
    setSelectedStatusOptions,
    handleDtInicVigChange,
    page,
    handleNextPage,
    handlePreviousPage,
    tableChangePage,
    qtItemsPage,
    setQtItemsPage,
    dtInicVig,
    handleDtFimVigChange,
    dtFimVig,
    search,
    handleSearch,
    defineFilters,
    currentSupplierId,
    supplierLoading,
    handleClearSearch,
    filter
  };
};
